import { useState, useEffect, useCallback } from 'react';
import { X, FileText, Search, Download, Star, RefreshCw, PlusCircle, Layers, Filter, Grid } from 'lucide-react';
import { API_URL } from '../config';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  icon: string;
  author: string;
  downloads: number;
  rating: number;
  node_count?: number;
  edge_count?: number;
  created_at?: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface TemplatesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onUseTemplate: (nodes: any[], edges: any[], flowName: string) => void;
  userId?: string;
}

export default function TemplatesPanel({
  isOpen,
  onClose,
  onUseTemplate,
  userId = 'default',
}: TemplatesPanelProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateDetails, setTemplateDetails] = useState<any | null>(null);
  const [cloning, setCloning] = useState(false);
  const [sortBy, setSortBy] = useState<'downloads' | 'rating' | 'name' | 'created_at'>('downloads');
  const [minRating, setMinRating] = useState<number>(0);
  const [showFilters, setShowFilters] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/templates/list?only_public=true&sort_by=${sortBy}`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (minRating > 0) {
        url += `&min_rating=${minRating}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, sortBy, minRating]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/templates/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (e) {
      console.error('Error loading categories:', e);
    }
  }, []);

  const loadTemplateDetails = async (templateId: string) => {
    try {
      const res = await fetch(`${API_URL}/templates/${templateId}`);
      if (res.ok) {
        const data = await res.json();
        setTemplateDetails(data);
      }
    } catch (e) {
      console.error('Error loading template details:', e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadCategories();
    }
  }, [isOpen, loadTemplates, loadCategories]);

  useEffect(() => {
    if (selectedTemplate) {
      loadTemplateDetails(selectedTemplate.id);
    } else {
      setTemplateDetails(null);
    }
  }, [selectedTemplate]);

  const cloneTemplate = async () => {
    if (!selectedTemplate || !templateDetails) return;
    
    try {
      setCloning(true);
      const flowName = `${selectedTemplate.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${Date.now()}`;
      
      const res = await fetch(
        `${API_URL}/templates/${selectedTemplate.id}/clone?new_name=${encodeURIComponent(flowName)}&owner_id=${userId}`,
        { method: 'POST' }
      );
      
      if (res.ok) {
        const data = await res.json();
        onUseTemplate(data.nodes, data.edges, flowName);
        onClose();
      }
    } catch (e) {
      console.error('Error cloning template:', e);
    } finally {
      setCloning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-rose-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Layers className="w-6 h-6" />
            <h2 className="text-xl font-bold">Flow Templates</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Cerca template (nome, descrizione, tags)..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              />
            </div>
            
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
            >
              <option value="all">🗂️ Tutte le categorie</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg flex items-center gap-2 transition ${
                showFilters ? 'bg-pink-50 border-pink-500 text-pink-700' : 'hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtri
            </button>
            
            <button 
              onClick={loadTemplates}
              className="p-2 text-gray-500 hover:text-pink-600 transition"
              title="Ricarica"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Filtri Avanzati */}
          {showFilters && (
            <div className="flex items-center gap-4 pt-2 border-t bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Ordina per:</label>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                  className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-pink-500"
                >
                  <option value="downloads">📥 Più scaricati</option>
                  <option value="rating">⭐ Più votati</option>
                  <option value="name">🔤 Nome A-Z</option>
                  <option value="created_at">📅 Più recenti</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Rating minimo:</label>
                <select
                  value={minRating}
                  onChange={e => setMinRating(Number(e.target.value))}
                  className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-pink-500"
                >
                  <option value="0">Tutti</option>
                  <option value="3">⭐ 3+</option>
                  <option value="4">⭐⭐ 4+</option>
                  <option value="4.5">⭐⭐⭐ 4.5+</option>
                </select>
              </div>
              
              <div className="text-xs text-gray-500 ml-auto">
                {templates.length} template{templates.length !== 1 ? 's' : ''} trovati
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Template List */}
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 mx-auto animate-spin text-pink-500" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun template trovato</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {templates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                      selectedTemplate?.id === template.id ? 'border-pink-500 bg-pink-50 shadow-lg' : 'hover:border-pink-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{template.icon}</span>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{template.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                        
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                            {template.category}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {template.downloads}
                          </span>
                          <span className="text-xs text-amber-500 flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            {template.rating.toFixed(1)}
                          </span>
                          {template.node_count !== undefined && (
                            <span className="text-xs text-blue-500 flex items-center gap-1">
                              <Grid className="w-3 h-3" />
                              {template.node_count} nodi
                            </span>
                          )}
                        </div>
                        
                        {template.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-xs bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Template Preview */}
          {selectedTemplate && templateDetails && (
            <div className="w-96 border-l bg-gray-50 p-4 overflow-auto">
              <div className="text-4xl mb-4 text-center">{templateDetails.icon}</div>
              <h2 className="text-xl font-bold mb-2">{templateDetails.name}</h2>
              <p className="text-gray-600 mb-4">{templateDetails.description}</p>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-bold text-sm mb-2">📊 Struttura</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Nodi:</span>
                  <span className="font-bold">{templateDetails.flow_definition?.nodes?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Connessioni:</span>
                  <span className="font-bold">{templateDetails.flow_definition?.edges?.length || 0}</span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-bold text-sm mb-2">🏷️ Tags</h3>
                <div className="flex flex-wrap gap-1">
                  {templateDetails.tags?.map((tag: string) => (
                    <span key={tag} className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-bold text-sm mb-2">👤 Autore</h3>
                <p className="text-sm text-gray-600">{templateDetails.author}</p>
              </div>
              
              <button
                onClick={cloneTemplate}
                disabled={cloning}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg font-bold hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {cloning ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-5 h-5" />
                    Usa questo Template
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

